/**
 * Passkeys, next to the auth code — never inside `SignIn`.
 *
 * `02-login.md` is explicit and this file is the reason it can be: the component
 * owns no WebAuthn call, it takes `passkey={{ name, onpasskey }}` and renders. All
 * of the mechanism — support detection, the three-legged sign-in, the enrolment
 * offer, the silent fallback — lives here, beside `auth.ts`, because the
 * robustness of signing in belongs with the sign-in code and because `steyn-design`
 * is consumed by six repositories at a tag. A bad tag must not be able to break
 * sign-in everywhere at once.
 *
 * ## The mechanism, in three legs
 *
 * Firebase Auth has no WebAuthn support, so:
 *
 *   1. `navigator.credentials.get()` here, via `@simplewebauthn/browser`;
 *   2. `fabric-api` verifies the assertion and mints a Firebase **custom token**;
 *   3. `signInWithCustomToken()` here, and from that moment the session is
 *      indistinguishable from a Google one — same uid, same claims, same
 *      `onIdTokenChanged`.
 *
 * ## One passkey cannot cover both sites — DEVIATIONS.md D7
 *
 * A passkey is bound to a Relying Party ID, which must be a registrable domain.
 * The estate spans two: `helloliam.co.za` (eight portals) and `helloeben.co.za`
 * (Front Door). A credential enrolled on one **cannot be used on the other** —
 * the browser refuses before any request is made. So Front Door enrols its own,
 * and the copy in `enrolNote()` says so out loud rather than letting somebody
 * discover it as a failure on the second site.
 *
 * ## Dormant until the Google path is confirmed
 *
 * `06-build-order.md`: *"do not build the passkey path on top of a broken Google
 * path"*. The Safari IndexedDB fault was fixed on 2026-08-15 and the owner has
 * not yet confirmed it. Enrolment happens after a Google sign-in, so a flaky
 * Google path makes an unreproducible passkey bug. `VITE_PASSKEY_ENABLED` is
 * therefore off by default and every entry point below returns early when it is
 * — the code ships, nothing calls it, and turning it on is one line in `.env`
 * once sign-in has been confirmed working.
 */
import { startAuthentication, startRegistration, browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { signInWithCustomToken } from "firebase/auth";
import { fabricAuth, getIdToken } from "./auth";

/**
 * The flag that keeps this dormant. Named for what it gates rather than for the
 * feature, so `grep PASSKEY` finds it and nobody has to guess whether "enabled"
 * means built or means live.
 */
const ENABLED = import.meta.env.VITE_PASSKEY_ENABLED === "true";

const BASE = import.meta.env.VITE_FABRIC_API_URL ?? "";

/**
 * The local hint — "this browser has a credential for this site".
 *
 * The one question WebAuthn will not answer. There is no way to ask "do I hold a
 * passkey for this RP?" without opening the platform sheet, and opening a Face ID
 * sheet on a device with nothing in it is the exact opposite of the brief. So the
 * hint is written when enrolment succeeds and read to decide whether the screen
 * renders passkey-first.
 *
 * `localStorage` is per **origin** while a credential is per **site**, so a passkey
 * enrolled on `fabric.helloliam.co.za` is genuinely usable on
 * `vault.helloliam.co.za` while the hint there is missing. That costs one extra
 * Google sign-in on each new subdomain and is then repaired silently by
 * `reconcile()` below — which is the right way round: erring towards Google
 * costs a click, erring towards the passkey sheet costs a dead end.
 */
const HINT_KEY = "steyn.passkey.hint";

type Hint = { name: string; rpId: string };

function readHint(): Hint | null {
  try {
    const raw = localStorage.getItem(HINT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Hint>;
    return typeof v?.name === "string" && typeof v?.rpId === "string" ? { name: v.name, rpId: v.rpId } : null;
  } catch {
    // Safari in a locked-down configuration throws on localStorage rather than
    // returning null. A missing hint is a supported state; a thrown one is not.
    return null;
  }
}

function writeHint(hint: Hint | null): void {
  try {
    if (hint) localStorage.setItem(HINT_KEY, JSON.stringify(hint));
    else localStorage.removeItem(HINT_KEY);
  } catch {
    // Losing the hint means the next visit is Google-first. Slower, never broken.
  }
}

/** POST to fabric-api without a token — the two sign-in legs have no session yet. */
async function anon<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j: { error?: string }) => j.error)
      .catch(() => null);
    throw new Error(detail ?? res.statusText);
  }
  return (await res.json()) as T;
}

/** POST with the caller's Fabric token — enrolment only, which is signed in. */
async function authed<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error("Not signed in");
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j: { error?: string }) => j.error)
      .catch(() => null);
    throw new Error(detail ?? res.statusText);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * Did the person decline, dismiss or time out?
 *
 * `NotAllowedError` is what the platform returns for "cancelled the sheet",
 * "timed out" and "no credential matched" alike — it is deliberately vague, so
 * that a site cannot learn which. That vagueness is fine here, because all three
 * want the same answer: fall back to Google **silently**, with a plain sentence.
 * Reopening a sheet somebody just dismissed is how software feels possessed.
 */
function isDismissal(err: unknown): boolean {
  const name = String((err as { name?: string }).name ?? "");
  return name === "NotAllowedError" || name === "AbortError";
}

class Passkey {
  /** Built, but off until the Google path is confirmed. See the header. */
  readonly enabled = ENABLED;

  /** This browser can do platform WebAuthn at all. */
  supported = $state(false);

  /** The registrable domain this site's passkeys are scoped to. */
  rpId = $state<string | null>(null);

  /** Set when this browser has a credential for this site. Drives the button. */
  name = $state<string | null>(null);

  /** True when a signed-in person could usefully be offered one. */
  offer = $state(false);

  busy = $state(false);

  /**
   * The plain sentence shown when the passkey path steps aside. Never a code,
   * never an alarm — the Google button is right there and it works.
   */
  note = $state<string | null>(null);

  /** Render `SignIn` passkey-first only when every part of the path is real. */
  get ready(): boolean {
    return this.enabled && this.supported && this.rpId !== null && this.name !== null;
  }

  /**
   * Work out, before the gate paints, whether the fast path exists here.
   *
   * Returns immediately when the flag is off, so a dormant build makes no request
   * and pays nothing. `/passkeys/rp` is unauthenticated and answers only about
   * the *origin* — never about an account, because on the one public page in the
   * estate "does Christo have a passkey?" would be an enumeration oracle.
   */
  async probe(): Promise<void> {
    if (!this.enabled) return;
    if (!browserSupportsWebAuthn()) return;

    // A roaming key would work, but the brief is Face ID in under two seconds and
    // a screen that says "Continue as Christo" should not open a sheet asking for
    // a USB stick. Platform authenticator or nothing.
    this.supported = await platformAuthenticatorIsAvailable().catch(() => false);
    if (!this.supported) return;

    try {
      const res = await fetch(`${BASE}/passkeys/rp`);
      if (!res.ok) return;
      const rp = (await res.json()) as { rpId: string | null };
      this.rpId = rp.rpId;
    } catch {
      // Fabric unreachable is a different failure from Google unreachable, and
      // only one of them stops you signing in. Stay quiet and stay Google-first.
      return;
    }

    const hint = readHint();
    // A hint left over from a different site is not this site's credential. It
    // can happen on a shared apex, and honouring it would show "Continue as
    // Christo" against a credential the browser will refuse to produce.
    this.name = hint && hint.rpId === this.rpId ? hint.name : null;
  }

  /**
   * The fast path. Three legs, and a silent fall back to Google if any gives way.
   *
   * Throws nothing the caller has to classify: it returns `false` and leaves a
   * sentence in `note`, because every failure here has the same remedy and the
   * screen already has the button for it.
   */
  async signIn(): Promise<boolean> {
    if (!this.ready) return false;
    this.busy = true;
    this.note = null;
    try {
      const options = await anon<PublicKeyCredentialRequestOptionsJSON>("/passkeys/signin/options", {});
      const response = await startAuthentication({ optionsJSON: options });
      const { customToken } = await anon<{ customToken: string }>("/passkeys/signin/verify", { response });

      // Leg three. From here the session is a normal Firebase session and
      // `onIdTokenChanged` in stores.svelte.ts picks it up like any other.
      await signInWithCustomToken(fabricAuth, customToken);
      return true;
    } catch (err) {
      if (isDismissal(err)) {
        this.note = "No passkey then — carry on with Google.";
      } else {
        // Logged in full, said plainly on screen. The one thing this must not do
        // is present as a fault in the house: the Google button below it works.
        console.warn("[passkey] sign-in did not complete", err);
        this.note = "The passkey did not work this time. Google will still let you in.";
        /**
         * A credential the server no longer holds — removed from the account, or
         * enrolled against a Fabric that has since been reseeded. The hint is
         * what is wrong, so clear it: leaving it would offer the same dead
         * button on every visit forever.
         */
        if (/not registered|different address|no longer exists/i.test(String((err as Error).message))) {
          writeHint(null);
          this.name = null;
        }
      }
      return false;
    } finally {
      this.busy = false;
    }
  }

  /**
   * Called after a successful Google sign-in — never before.
   *
   * Two jobs, and the first one is why this is not simply "show the offer".
   * Because the hint is per origin and the credential is per site, somebody who
   * enrolled on Fabric and then opens Front Door for the first time already has a
   * usable credential for `helloliam.co.za` but no hint on `helloeben.co.za`.
   * Asking `GET /passkeys` — which needs the session that has just been
   * established — repairs the hint silently instead of offering to enrol a
   * second credential they do not need.
   */
  async reconcile(displayName: string): Promise<void> {
    if (!this.enabled || !this.supported || !this.rpId) return;
    try {
      const { passkeys } = await authed<{ passkeys: Array<{ rpId: string }> }>("GET", "/passkeys");
      const here = passkeys.some((p) => p.rpId === this.rpId);
      if (here) {
        this.name = displayName;
        writeHint({ name: displayName, rpId: this.rpId });
        this.offer = false;
      } else {
        this.offer = true;
      }
    } catch {
      // No offer rather than a wrong one. This runs on every signed-in load and
      // has no business turning a Fabric hiccup into a prompt.
      this.offer = false;
    }
  }

  /**
   * The sentence beside the offer. Honest about D7 rather than quietly wrong.
   *
   * Somebody who saves a passkey here and then finds the portals still asking for
   * Google would reasonably conclude the passkey did not save. Saying it up front
   * costs one line and prevents that.
   */
  enrolNote(): string {
    return this.rpId
      ? `Saves a passkey for ${this.rpId}. The portals on the other address will ask for one of their own — passkeys are tied to a single web address.`
      : "Saves a passkey for this address.";
  }

  /** Save one. Skippable, and dismissal is remembered for this browser. */
  async enrol(displayName: string): Promise<boolean> {
    if (!this.enabled || !this.rpId) return false;
    this.busy = true;
    this.note = null;
    try {
      const options = await authed<PublicKeyCredentialCreationOptionsJSON>("POST", "/passkeys/enrol/options");
      const response = await startRegistration({ optionsJSON: options });
      await authed("POST", "/passkeys/enrol/verify", { response });

      this.name = displayName;
      writeHint({ name: displayName, rpId: this.rpId });
      this.offer = false;
      return true;
    } catch (err) {
      if (!isDismissal(err)) console.warn("[passkey] enrolment did not complete", err);
      // Either way the offer goes away for this session. Someone who declined
      // once should not be asked again on the next page load, and someone whose
      // enrolment failed is not helped by being asked to repeat it immediately.
      this.offer = false;
      return false;
    } finally {
      this.busy = false;
    }
  }

  /** "Not now." Same outcome as a failure, deliberately: the offer disappears. */
  skip(): void {
    this.offer = false;
  }
}

export const passkey = new Passkey();
